import IdentityIcon from "@assets/icons/identity.svg";
import LockIcon from "@assets/icons/lock.svg";
import ShieldIcon from "@assets/icons/shield.svg";
import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { WordingKey } from "@typings/i18n-typings";
import { SVGComponent } from "@typings/svg-component";
import { ProfessionalInformationValidationSteps } from "@ui/pages/validate-professional-information/components/register-progression-bar";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint } from "@ui/style";
import { colors } from "@ui/style/colors";
import React from "react";

interface RegisterDescriptionProfInfoProps {
	step:
		| ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION
		| ProfessionalInformationValidationSteps.PROFESSIONAL_INFORMATION;
}

type RegisterStepsWithDescription =
	| ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION
	| ProfessionalInformationValidationSteps.PROFESSIONAL_INFORMATION;

const stepDataDescription = {
	[ProfessionalInformationValidationSteps.PROFESSIONAL_INFORMATION]: {
		title: "register.description.professionalinformation.title",
		note1: "register.description.professionalinformation.note1",
		note2: "register.description.professionalinformation.note2",
		icon: ShieldIcon,
	},
	[ProfessionalInformationValidationSteps.IDENTITY_VERIFICATION]: {
		title: "register.description.checkidentity.title",
		note1: "register.description.checkidentity.note1",
		note2: "register.description.checkidentity.note2",
		icon: IdentityIcon,
	},
} satisfies Record<
	RegisterStepsWithDescription,
	{
		title: WordingKey;
		note1: WordingKey;
		note2: WordingKey;
		icon: SVGComponent;
	}
>;

export default function RegisterDescriptionProfInfo({ step }: RegisterDescriptionProfInfoProps): JSX.Element {
	const { format } = useI18n();

	const { icon: Icon, title, note1, note2 } = stepDataDescription[step];

	return (
		<ContentRight>
			<StyledSubTitle>{format(title)}</StyledSubTitle>
			<NotesContainer>
				<StyledIcon>
					<Icon width={16} color={colors.white} />
				</StyledIcon>
				<div className="p-size-m">{format(note1)}</div>
				<StyledIcon>
					<LockIcon width={16} color={colors.white} />
				</StyledIcon>
				<div className="p-size-m">{format(note2)}</div>
			</NotesContainer>
		</ContentRight>
	);
}

const ContentRight = styled.div`
	position: sticky;
	width: 50%;
	top: 100px;
	z-index: 2;
	color: ${colors.grey};
	padding-top: 140px;

	@media ${physioMeTabletBreakpoint} {
		padding: 48px 32px;
		width: 100%;
	}
	@media ${physioMeMobileBreakpoint} {
		padding: 48px 16px;
	}
`;

const StyledSubTitle = styled.h2`
	font-size: 24px;
	margin-bottom: 32px;
`;

const NotesContainer = styled.div`
	display: grid;
	grid-template-columns: auto 1fr;
	gap: 16px;
	line-height: 32px;
`;

const StyledIcon = styled.div`
	background-color: ${colors.primary};
	border-radius: 16px;
	padding: 8px;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;
